/**
 * Hook to fetch deposit and withdraw history for x10000 mode
 */

import { useMemo, useEffect, useState } from "react";
import useSWR, { SWRConfiguration } from "swr";
import { useAccount } from "wagmi";

import { getDepositHistory } from "./deposit";
import { getWithdrawHistory, getStoredToken, getLastAddress } from "./client";
import type { DepositRecord, WithdrawRecord } from "../types";

const defaultConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  refreshInterval: 30000, // Refresh every 30 seconds
};

export interface X10000FundingHistoryItem {
  id: string;
  type: "deposit" | "withdraw";
  token: string;
  amount: string;
  tx_hash: string | null;
  status: string;
  created_at: number;
  // Additional fields for withdraw
  nonce?: number;
  expiry?: number;
  backend_signature?: string;
}

type UseX10000FundingHistoryResult = {
  fundingHistory: X10000FundingHistoryItem[] | undefined;
  isLoading: boolean;
  error?: Error;
  mutate: () => void;
};

export function useX10000FundingHistory(
  chainId: number | undefined,
  config?: SWRConfiguration
): UseX10000FundingHistoryResult {
  const { address: account, chainId: wagmiChainId } = useAccount();
  const effectiveChainId = chainId || wagmiChainId;
  
  // Use state to track token existence so component re-renders when token changes
  const [hasToken, setHasToken] = useState(() => {
    // Use same logic as apiFetch: try account first, then fallback to last address
    let targetAddress = account;
    if (!targetAddress) {
      targetAddress = getLastAddress();
    }
    const token = getStoredToken(targetAddress, effectiveChainId);
    return token !== null;
  });

  // Poll for token changes to trigger re-render
  useEffect(() => {
    const checkToken = () => {
      // Use same logic as apiFetch: try account first, then fallback to last address
      let targetAddress = account;
      if (!targetAddress) {
        targetAddress = getLastAddress();
      }
      const token = getStoredToken(targetAddress, effectiveChainId);
      const newHasToken = token !== null;
      setHasToken((prev) => {
        if (prev !== newHasToken) {
          return newHasToken;
        }
        return prev;
      });
    };

    checkToken();
    const interval = setInterval(checkToken, 500);
    
    // Also listen for token change events
    const handleTokenChange = () => {
      checkToken();
    };
    window.addEventListener("x10000-token-change", handleTokenChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("x10000-token-change", handleTokenChange);
    };
  }, [account, effectiveChainId]);
  
  const authenticated = hasToken;
  const depositKey = effectiveChainId && account && authenticated ? [`x10000-deposit-history`, effectiveChainId] : null;
  const withdrawKey = effectiveChainId && account && authenticated ? [`x10000-withdraw-history`, effectiveChainId] : null;

  // Fetch deposit history
  const {
    data: depositData,
    error: depositError,
    isLoading: isDepositLoading,
    mutate: mutateDeposits,
  } = useSWR<{ deposits: DepositRecord[] }>(
    depositKey,
    async () => {
      try {
        const result = await getDepositHistory(effectiveChainId!);
        console.log("[useX10000FundingHistory] ✅ Deposit history fetched", { depositCount: result.deposits?.length || 0 });
        return result;
      } catch (err) {
        console.error("[useX10000FundingHistory] ❌ Deposit history fetch error", err);
        throw err;
      }
    },
    { 
      ...defaultConfig, 
      ...config,
      onError: (err) => {
        console.error("[useX10000FundingHistory] Deposit onError", err);
        config?.onError?.(err);
      },
    }
  );

  // Fetch withdraw history
  const {
    data: withdrawData,
    error: withdrawError,
    isLoading: isWithdrawLoading,
    mutate: mutateWithdraws,
  } = useSWR<{ withdrawals: WithdrawRecord[] }>(
    withdrawKey,
    async () => {
      try {
        const result = await getWithdrawHistory(effectiveChainId!);
        console.log("[useX10000FundingHistory] ✅ Withdraw history fetched", { withdrawCount: result.withdrawals?.length || 0 });
        return result;
      } catch (err) {
        console.error("[useX10000FundingHistory] ❌ Withdraw history fetch error", err);
        throw err;
      }
    },
    { 
      ...defaultConfig, 
      ...config,
      onError: (err) => {
        console.error("[useX10000FundingHistory] Withdraw onError", err);
        config?.onError?.(err);
      },
    }
  );

  // Combine and sort by created_at (newest first)
  const fundingHistory = useMemo(() => {
    const items: X10000FundingHistoryItem[] = [];

    // Add deposits
    if (depositData?.deposits) {
      for (const deposit of depositData.deposits) {
        items.push({
          id: deposit.id,
          type: "deposit",
          token: deposit.token,
          amount: deposit.amount,
          tx_hash: deposit.tx_hash,
          status: deposit.status,
          created_at: deposit.created_at,
        });
      }
    }

    // Add withdrawals
    if (withdrawData?.withdrawals) {
      for (const withdraw of withdrawData.withdrawals) {
        items.push({
          id: withdraw.id,
          type: "withdraw",
          token: withdraw.token,
          amount: withdraw.amount,
          tx_hash: withdraw.tx_hash || null,
          status: withdraw.status,
          created_at: withdraw.created_at,
          nonce: withdraw.nonce,
          expiry: withdraw.expiry,
          backend_signature: withdraw.backend_signature,
        });
      }
    }

    // Sort by created_at descending (newest first)
    items.sort((a, b) => b.created_at - a.created_at);

    return items;
  }, [depositData?.deposits, withdrawData?.withdrawals]);

  const mutate = () => {
    mutateDeposits();
    mutateWithdraws();
  };

  return {
    fundingHistory,
    isLoading: isDepositLoading || isWithdrawLoading,
    error: depositError || withdrawError,
    mutate,
  };
}

